// Globals
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Modules
import { selectChannel } from '@/modules/Channels';
import { Header } from '@/modules/Layout';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { updateAiBotSettings } from '../../feature';

// Components
import { Button, Spinner } from '@/ui';
import { useForm } from '@/components';
import { AiBotConfigForm } from '../../components';
import { StyledContainer } from './AiBotConfigContainerStyles';

// Helpers
import { useGetAiBotSettings } from '../../hooks';
import { getAiBotConfigInitialValues, TAiBotConfigFormValues } from '../../helpers';
import { UpdateAiBotSettingsDTO } from '../../dtos';

const AiBotConfigContainer = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const channel = useAppState(selectChannel);
  const { isLoading, settings } = useGetAiBotSettings({ channelSlug: channel?.slug });
  const [form] = useForm();
  const initialValues = useMemo(() => getAiBotConfigInitialValues(settings, t), [settings, t]);

  useEffect(() => {
    if (settings) {
      form.setFieldsValue(getAiBotConfigInitialValues(settings, t));
    }
  }, [settings, t, form]);

  const handleSubmit = async (values: TAiBotConfigFormValues) => {
    if (!channel) return;

    const dto = new UpdateAiBotSettingsDTO({ ...values, channelSlug: channel.slug });

    try {
      await dispatch(updateAiBotSettings(dto)).unwrap();
    } catch {}
  };

  return (
    <>
      <Header title={t('menu_ai_bot_configuration')}>
        <Button type="primary" loading={isLoading} onClick={form.submit}>
          {t('btn_save')}
        </Button>
      </Header>
      <Spinner spinning={isLoading}>
        <StyledContainer>
          <AiBotConfigForm form={form} initialValues={initialValues} onSubmit={handleSubmit} isLoading={isLoading} />
        </StyledContainer>
      </Spinner>
    </>
  );
};

export default AiBotConfigContainer;
